/**
 * @file datetime.js
 * @description Helpers centralizados de fechas y zonas horarias para el
 *              Dashboard. Pareja del `lib/datetime.js` en el API.
 *
 *              ## Contrato (UC-REG-079)
 *
 *              - El API recibe + guarda + devuelve SIEMPRE UTC.
 *              - Este front convierte UTC → hora del tenant al renderear.
 *              - Este front convierte hora del tenant → UTC al enviar al API.
 *              - Si el código antiguo formatea con `moment().format(...)`
 *                sin pasar timezone, asume el TZ del browser (variable
 *                según el usuario) → BUG. Reemplazar por `formatAR(...)`.
 *              - Si el código antiguo manda al backend `moment(...).format(...)`
 *                sin offset, el backend lo interpreta como UTC → BUG.
 *                Reemplazar por `toApiUTC(...)`.
 *
 *              ## Timezone del tenant
 *
 *              Hoy: `America/Argentina/Buenos_Aires` (UTC-3, sin DST).
 *              Se lee de `store.state.organizationContext.timezone` con
 *              fallback hardcoded. Si vendemos en otro país (nuevo tenant),
 *              el `Organization.timezone` se cambia desde el Platform
 *              Admin y este helper lo respeta automáticamente. No hay
 *              multi-tz dentro de un mismo tenant (decisión de Federico
 *              2026-05-31).
 *
 *              ## API de este módulo
 *
 *              - `formatAR(date, fmt)` — render humano en TZ del tenant.
 *              - `formatDateAR(date)` — `DD/MM/YYYY` corto.
 *              - `formatTimeAR(date)` — `HH:mm` corto.
 *              - `formatDateTimeAR(date)` — `DD/MM/YYYY HH:mm`.
 *              - `parseUserInputAR(str)` — input del usuario (sin TZ) →
 *                Date UTC.
 *              - `toApiUTC(date)` — input del componente → string ISO UTC
 *                para mandar al API.
 *              - `nowAR()` / `startOfDayAR()` / `endOfDayAR()` —
 *                boundaries en calendario del tenant.
 */

import moment from "moment";
import store from "@/store";

export const FALLBACK_TZ = "America/Argentina/Buenos_Aires";

/**
 * @description Devuelve el timezone IANA configurado para el tenant
 *              actual. Lee del organizationContext (hidratado al login)
 *              con fallback hardcoded a Argentina si todavía no se cargó.
 * @returns {string} timezone IANA
 */
export function getTenantTz() {
  try {
    const ctx = store && store.state && store.state.organizationContext;
    if (ctx && ctx.timezone) return ctx.timezone;
  } catch (_) {
    // ignored
  }
  return FALLBACK_TZ;
}

/**
 * @description Calcula el offset (minutos) entre UTC y el TZ del tenant
 *              en la fecha de referencia indicada. Usa `Intl` nativo —
 *              respeta DST si el TZ lo tiene.
 *
 *              Para Argentina (sin DST): siempre `-180`.
 *              Para tenants futuros con DST: varía según fecha.
 * @param {Date} [refDate] - fecha de referencia. Default: ahora.
 * @returns {number} offset en minutos (negativo si oeste de UTC)
 */
export function getTenantOffsetMinutes(refDate = new Date()) {
  const tz = getTenantTz();
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset",
    });
    const parts = formatter.formatToParts(refDate);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    if (offsetPart && offsetPart.value.startsWith("GMT")) {
      const offsetStr = offsetPart.value.replace("GMT", "");
      if (!offsetStr) return 0;
      const sign = offsetStr[0] === "+" ? 1 : -1;
      const [hh, mm] = offsetStr.substring(1).split(":");
      return sign * (parseInt(hh, 10) * 60 + parseInt(mm || "0", 10));
    }
  } catch (_) {
    // browser sin soporte longOffset → fallback hardcoded
  }
  return -180; // Argentina por default
}

/**
 * @description Formatea una fecha en el TZ del tenant con el patrón pedido.
 *              Acepta Date o string ISO (típicamente UTC del API).
 * @param {Date|string} date
 * @param {string} [fmt] - patrón moment.js. Default: "DD/MM/YYYY HH:mm".
 * @returns {string} fecha formateada en TZ del tenant; "" si inválido.
 */
export function formatAR(date, fmt = "DD/MM/YYYY HH:mm") {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const offsetMin = getTenantOffsetMinutes(d);
  return moment(d).utcOffset(offsetMin).format(fmt);
}

/**
 * @description Shortcut: solo fecha en formato DD/MM/YYYY.
 * @param {Date|string} date
 * @returns {string}
 */
export function formatDateAR(date) {
  return formatAR(date, "DD/MM/YYYY");
}

/**
 * @description Shortcut: solo hora en formato HH:mm.
 * @param {Date|string} date
 * @returns {string}
 */
export function formatTimeAR(date) {
  return formatAR(date, "HH:mm");
}

/**
 * @description Shortcut: fecha + hora en DD/MM/YYYY HH:mm.
 * @param {Date|string} date
 * @returns {string}
 */
export function formatDateTimeAR(date) {
  return formatAR(date, "DD/MM/YYYY HH:mm");
}

/**
 * @description Parsea un string que viene del usuario (input field, query
 *              param, etc.). Si trae offset explícito, lo respeta. Si no,
 *              asume hora local del tenant. Devuelve Date en UTC interno.
 * @param {string|Date} input
 * @returns {Date|null}
 */
export function parseUserInputAR(input) {
  if (!input) return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }
  const str = String(input).trim();
  if (!str) return null;
  // Offset explícito (Z o ±HH:MM)
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(str)) {
    const m = moment.parseZone(str);
    return m.isValid() ? m.toDate() : null;
  }
  // Sin offset → asumir tenant TZ. utcOffset con keepLocalTime=true
  // mantiene la "wallclock" e infiere el UTC equivalente.
  const offsetMin = getTenantOffsetMinutes();
  const m = moment(str).utcOffset(offsetMin, true);
  return m.isValid() ? m.toDate() : null;
}

/**
 * @description Convierte una fecha (que vos manejaste localmente como
 *              hora del tenant) al string ISO UTC que el API espera.
 *              Garantiza que el backend NO interprete la fecha como UTC
 *              cuando en realidad era hora local.
 * @param {Date|string} date
 * @returns {string|null} ISO 8601 con 'Z' al final, o null si inválido.
 */
export function toApiUTC(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * @description Devuelve la fecha "ahora" como `Date` UTC. El nombre
 *              `nowAR` enfatiza que las funciones de boundaries
 *              (`startOfDayAR`/`endOfDayAR`) van a interpretar esta fecha
 *              en el calendario del tenant.
 * @returns {Date}
 */
export function nowAR() {
  return new Date();
}

/**
 * @description Inicio del día (00:00:00) en el calendario del tenant,
 *              como Date UTC equivalente. Útil para filtros tipo
 *              "appointments de hoy" donde "hoy" es el día del tenant.
 * @param {Date|string} [date] - referencia. Default: ahora.
 * @returns {Date}
 */
export function startOfDayAR(date) {
  const ref = date
    ? date instanceof Date
      ? date
      : new Date(date)
    : new Date();
  const offsetMin = getTenantOffsetMinutes(ref);
  return moment(ref).utcOffset(offsetMin).startOf("day").toDate();
}

/**
 * @description Fin del día (23:59:59.999) en el calendario del tenant,
 *              como Date UTC equivalente.
 * @param {Date|string} [date] - referencia. Default: ahora.
 * @returns {Date}
 */
export function endOfDayAR(date) {
  const ref = date
    ? date instanceof Date
      ? date
      : new Date(date)
    : new Date();
  const offsetMin = getTenantOffsetMinutes(ref);
  return moment(ref).utcOffset(offsetMin).endOf("day").toDate();
}

export default {
  FALLBACK_TZ,
  getTenantTz,
  getTenantOffsetMinutes,
  formatAR,
  formatDateAR,
  formatTimeAR,
  formatDateTimeAR,
  parseUserInputAR,
  toApiUTC,
  nowAR,
  startOfDayAR,
  endOfDayAR,
};
